import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useRefreshRelatedData } from '@/hooks/useRefreshRelatedData';
import { showApiErrorToast } from '@/utils/errors';
import { approveLeaveApplication, rejectLeaveApplication } from '../api/leaveApplications.api';
import { REVIEW_DECISION } from '../constants/leaveApplication.constants';
import type {
  ReviewDecision,
  ReviewLeaveApplicationPayload,
} from '../types/leaveApplication.types';

interface ReviewArgs {
  id: number;
  decision: ReviewDecision;
  payload: ReviewLeaveApplicationPayload;
  requestLabel: string;
}

interface ReviewMessage {
  success: string;
  failure: string;
}

function getReviewMessage(decision: ReviewDecision, requestLabel: string): ReviewMessage {
  if (decision === REVIEW_DECISION.APPROVE) {
    return {
      success: `${requestLabel} approved`,
      failure: `Could not approve the ${requestLabel.toLowerCase()}`,
    };
  }
  return {
    success: `${requestLabel} rejected`,
    failure: `Could not reject the ${requestLabel.toLowerCase()}`,
  };
}

export function useReviewLeaveApplication(onSuccess?: () => void) {
  const refreshRelatedData = useRefreshRelatedData();

  return useMutation({
    mutationFn: ({ id, decision, payload }: ReviewArgs) =>
      decision === REVIEW_DECISION.APPROVE
        ? approveLeaveApplication(id, payload)
        : rejectLeaveApplication(id, payload),
    onSuccess: (_data, { decision, requestLabel }) => {
      void refreshRelatedData('leaveApplication');
      toast.success(getReviewMessage(decision, requestLabel).success);
      onSuccess?.();
    },
    onError: (error, { decision, requestLabel }) => {
      void refreshRelatedData('leaveApplication');
      showApiErrorToast(error, getReviewMessage(decision, requestLabel).failure);
    },
  });
}
